import { Button } from "@growtherapy/sprout-ui";
import { minutesToMilliseconds } from "date-fns";
import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import { useEndOfSessionToast } from "../../hooks/useEndOfSessionToast";
import { toast } from "../../toasts";
import { roomAtom } from "../../video-sdk";
import { activeClientInformationTabAtom } from "../client-information/state";
import { hasRecordingAtom } from "../session-recording/state";
import {
  ClientInformationDrawerTab,
  ProviderDrawerState,
  drawerStateAtom,
} from "../state";
import { useSessionSummaryNudgeCount } from "../useSessionSummaryNudgeCount";
import { PromptFollowUpToast } from "./PromptFollowUpToast";
import { useTrackEvent } from "../../segment/segment";
import { EventTypeEnum } from "../../segment/types";

const NUDGE_PRIOR_END_TIME_MS = minutesToMilliseconds(5);

export function usePromptFollowUpToast(
  providerShortId: string | undefined,
  patientShortId: string | undefined,
) {
  const getIsInSession = useAtomCallback(
    useCallback((get) => !!get(roomAtom), []),
  );
  const setDrawerState = useSetAtom(drawerStateAtom);
  const setActiveTab = useSetAtom(activeClientInformationTabAtom);
  const hasRecording = useAtomValue(hasRecordingAtom);
  const { userClicked } = useTrackEvent();

  const {
    increment: incrementSessionSummaryNudgeCount,
    countBelowMax: showSessionSummaryNudge,
  } = useSessionSummaryNudgeCount();

  const openAppointments = useCallback(() => {
    setDrawerState(ProviderDrawerState.CLIENT_INFORMATION);
    setActiveTab(ClientInformationDrawerTab.APPOINTMENTS);
  }, [setActiveTab, setDrawerState]);

  const showToast = useCallback(() => {
    toast.enqueue(
      ({ closeToast, ...ariaProps }) => {
        const title =
          hasRecording && showSessionSummaryNudge
            ? "Documentation Tip"
            : "5 Minute Reminder";
        const subtitle =
          hasRecording && showSessionSummaryNudge ? (
            `Click "Complete Session" to process the AI summary immediately after the session.`
          ) : (
            // TODO(CARED-1599): toast should handle if a follow up is already scheduled
            <>
              Consider wrapping up your discussion, and setting up your next
              appointment from{" "}
              <Button
                onClick={() => {
                  closeToast();
                  openAppointments();
                  userClicked(
                    EventTypeEnum.FIVE_MINUTE_REMINDER_TOAST_SCHEDULING_PROMPT,
                    {
                      patientShortId: patientShortId ?? "",
                      providerShortId: providerShortId ?? "",
                    },
                  );
                }}
                use="unstyled"
                className="underline decoration-neutral-600 inline p-0"
              >
                the appointments tab
              </Button>
              .
            </>
          );

        return (
          <PromptFollowUpToast
            {...ariaProps}
            title={title}
            subtitle={subtitle}
          />
        );
      },
      {
        onClose:
          hasRecording && showSessionSummaryNudge
            ? incrementSessionSummaryNudgeCount
            : undefined,
        purpose: "prompt",
        timeout: minutesToMilliseconds(1),
      },
    );
  }, [
    hasRecording,
    incrementSessionSummaryNudgeCount,
    openAppointments,
    showSessionSummaryNudge,
    patientShortId,
    providerShortId,
    userClicked,
  ]);

  useEndOfSessionToast(
    providerShortId,
    patientShortId,
    showToast,
    NUDGE_PRIOR_END_TIME_MS,
    getIsInSession(),
  );
}
